import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "timeSlotInput",
    "customTimeFields",
    "eventSelection",
    "customEventFields",
    "roomSelect",
    "bookingDate",
    "customStartTime",
    "customEndTime",
    "roomPicker",
    "roomOption",
    "roomCheckbox",
    "roomStatus",
    "roomAvailabilityHint",
  ];

  static values = {
    other: String,
    availabilityUrl: String,
    multiRoom: Boolean,
    excludeBookingIds: String,
  };

  connect() {
    this.updateCustomTimeFields();
    this.updateCustomEventFields();
    if (this.multiRoomValue) this.refreshAvailability();
    this.dispatchRoomLocations();
  }

  timeSlotChanged() {
    this.updateCustomTimeFields();
  }

  scheduleChanged() {
    if (this.multiRoomValue) this.refreshAvailability();
  }

  roomSelectionChanged() {
    this.roomOptionTargets.forEach((option) => {
      const checkbox = option.querySelector('input[type="checkbox"]');
      option.classList.toggle("is-selected", checkbox && checkbox.checked);
    });
    this.dispatchRoomLocations();
  }

  roomSelectChanged() {
    this.dispatchRoomLocations();
  }

  dispatchRoomLocations() {
    const locations = this.selectedRoomLocations();
    document.dispatchEvent(
      new CustomEvent("room-booking:rooms-changed", { detail: { locations } })
    );
  }

  selectedRoomLocations() {
    const locationsById = {};

    if (this.multiRoomValue) {
      this.roomOptionTargets.forEach((option) => {
        const checkbox = option.querySelector('input[type="checkbox"]');
        if (!checkbox || !checkbox.checked) return;

        const locationId = parseInt(option.dataset.locationId, 10);
        const locationName = option.dataset.locationName;
        if (locationId) {
          locationsById[locationId] = { id: locationId, name: locationName };
        }
      });
    } else if (this.hasRoomSelectTarget) {
      const selected = this.roomSelectTarget.selectedOptions[0];
      if (selected) {
        const locationId = parseInt(selected.dataset.locationId, 10);
        const locationName = selected.dataset.locationName;
        if (locationId) {
          locationsById[locationId] = { id: locationId, name: locationName };
        }
      }
    }

    return Object.values(locationsById);
  }

  eventSelectionChanged() {
    this.updateCustomEventFields();
  }

  selectRoom(event) {
    event.preventDefault();
    if (!this.hasRoomSelectTarget) return;

    const roomId = event.currentTarget.dataset.roomId;
    if (!roomId) return;

    this.roomSelectTarget.value = roomId;
    this.roomSelectTarget.focus();
  }

  async refreshAvailability() {
    if (!this.hasRoomPickerTarget || !this.hasBookingDateTarget) return;

    const date = this.bookingDateTarget.value;
    const timeSlot = this.selectedTimeSlot();
    const customStart = this.hasCustomStartTimeTarget ? this.customStartTimeTarget.value : "";
    const customEnd = this.hasCustomEndTimeTarget ? this.customEndTimeTarget.value : "";

    if (!date) {
      this.clearAvailability("Select a date to see room availability.");
      return;
    }

    if (timeSlot === "custom" && (!customStart || !customEnd)) {
      this.clearAvailability("Enter start and end times to check availability.");
      return;
    }

    const params = new URLSearchParams({ date, time_slot: timeSlot });
    if (timeSlot === "custom") {
      params.set("custom_start_time", customStart);
      params.set("custom_end_time", customEnd);
    }
    if (this.excludeBookingIdsValue) {
      params.set("exclude_booking_ids", this.excludeBookingIdsValue);
    }

    try {
      const response = await fetch(`${this.availabilityUrlValue}?${params.toString()}`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });

      if (!response.ok) return;

      const rooms = await response.json();
      this.applyAvailability(rooms);
    } catch (_error) {
      // Leave current state if the request fails.
    }
  }

  applyAvailability(rooms) {
    const availabilityById = {};
    rooms.forEach((room) => {
      availabilityById[String(room.id)] = room;
    });

    let availableCount = 0;

    this.roomOptionTargets.forEach((option) => {
      const roomId = option.dataset.roomId;
      const status = availabilityById[roomId];
      const checkbox = option.querySelector('input[type="checkbox"]');
      const statusTarget = option.querySelector('[data-room-booking-form-target="roomStatus"]');

      if (!status || !statusTarget || !checkbox) return;

      statusTarget.hidden = false;
      this.fillRoomStatus(statusTarget, status);
      statusTarget.classList.remove("rb-pill--success", "rb-pill--neutral", "rb-pill--muted");

      if (status.available) {
        availableCount += 1;
        statusTarget.classList.add("rb-pill--success");
        checkbox.disabled = false;
        option.classList.remove("is-unavailable");
      } else {
        statusTarget.classList.add("rb-pill--muted");
        checkbox.checked = false;
        checkbox.disabled = true;
        option.classList.remove("is-selected");
        option.classList.add("is-unavailable");
      }
    });

    if (this.hasRoomAvailabilityHintTarget) {
      this.roomAvailabilityHintTarget.textContent = `${availableCount} of ${rooms.length} rooms available for this time.`;
    }
  }

  fillRoomStatus(statusTarget, status) {
    const label = statusTarget.querySelector("[data-room-status-label]");
    const link = statusTarget.querySelector("[data-room-status-link]");
    const text = status.detail ? `${status.label} · ${status.detail}` : status.label;

    statusTarget.title = text;
    if (label) {
      label.textContent = text;
    } else {
      statusTarget.textContent = text;
    }

    if (!link) return;

    if (status.url) {
      link.hidden = false;
      link.href = status.url;
      link.textContent = "Open booking";
    } else {
      link.hidden = true;
      link.removeAttribute("href");
    }
  }

  clearAvailability(message) {
    this.roomOptionTargets.forEach((option) => {
      const checkbox = option.querySelector('input[type="checkbox"]');
      const statusTarget = option.querySelector('[data-room-booking-form-target="roomStatus"]');
      if (checkbox) {
        checkbox.disabled = false;
      }
      option.classList.remove("is-unavailable");
      if (statusTarget) statusTarget.hidden = true;
    });

    if (this.hasRoomAvailabilityHintTarget) {
      this.roomAvailabilityHintTarget.textContent = message;
    }
  }

  selectedTimeSlot() {
    const selected = this.timeSlotInputTargets.find((input) => input.checked);
    return selected ? selected.value : "full_day";
  }

  updateCustomTimeFields() {
    if (!this.hasCustomTimeFieldsTarget) return;

    const selected = this.timeSlotInputTargets.find((input) => input.checked);
    const isCustom = Boolean(selected) && selected.value === "custom";
    this.customTimeFieldsTarget.classList.toggle("d-none", !isCustom);
  }

  updateCustomEventFields() {
    if (!this.hasCustomEventFieldsTarget || !this.hasEventSelectionTarget) return;

    const isOther = this.eventSelectionTarget.value === this.otherValue;
    this.customEventFieldsTarget.classList.toggle("d-none", !isOther);

    if (isOther) {
      const input = this.customEventFieldsTarget.querySelector("input");
      if (input && document.activeElement !== input) input.focus();
    }
  }
}
